import PropTypes from 'prop-types';
import { BsArrowLeftRight, BsPencil, BsTrash } from 'react-icons/bs';

import Select from '#globalComponents/Forms/Select';
import IconButton from '#globalComponents/IconButton/IconButton';
import Tag from '#globalComponents/Tag/Tag';
import { formatCount } from '#lib/numberFormat';

import { MemberBudgetEditor } from './MemberBudgetEditor';
import RemoveConfirmation from './RemoveConfirmation';

const ROLE_LABELS = {
	owner: 'Eigentümer',
	admin: 'Admin',
	member: 'Mitglied',
};

const ROLE_TONES = {
	owner: 'primary',
	admin: 'primary',
	member: 'neutral',
};

const RoleDisplay = ({ member, isOwner, isCurrentUser, onRoleChange, changeRolePending }) => {
	const canChangeRole = isOwner && !isCurrentUser && member.role !== 'owner';

	if (canChangeRole) {
		return (
			<Select
				aria-label="Rolle ändern"
				value={member.role}
				onChange={(e) => onRoleChange(member.userId, e.target.value)}
				disabled={changeRolePending}
				className="w-32"
			>
				<option value="admin">Admin</option>
				<option value="member">Mitglied</option>
			</Select>
		);
	}

	return <Tag label={ROLE_LABELS[member.role] || member.role} tone={ROLE_TONES[member.role] || 'neutral'} />;
};

RoleDisplay.propTypes = {
	member: PropTypes.object.isRequired,
	isOwner: PropTypes.bool.isRequired,
	isCurrentUser: PropTypes.bool.isRequired,
	onRoleChange: PropTypes.func.isRequired,
	changeRolePending: PropTypes.bool.isRequired,
};

const MemberActions = ({ member, isCurrentUser, onTransfer, onToggleBudget, onToggleRemove }) => {
	const canEditBudget = isCurrentUser || member.role !== 'owner';
	const canRemove = !isCurrentUser && member.role !== 'owner';

	return (
		<div className="flex gap-1">
			<IconButton
				icon={BsArrowLeftRight}
				label="Credits übertragen"
				tone="primary"
				onClick={() => onTransfer(member.userId)}
			/>
			{canEditBudget && (
				<IconButton
					icon={BsPencil}
					label="Budget bearbeiten"
					tone="primary"
					onClick={() => onToggleBudget(member.userId)}
				/>
			)}
			{canRemove && (
				<IconButton
					icon={BsTrash}
					label="Mitglied entfernen"
					tone="danger"
					onClick={() => onToggleRemove(member.userId)}
				/>
			)}
		</div>
	);
};

MemberActions.propTypes = {
	member: PropTypes.object.isRequired,
	isCurrentUser: PropTypes.bool.isRequired,
	onTransfer: PropTypes.func.isRequired,
	onToggleBudget: PropTypes.func.isRequired,
	onToggleRemove: PropTypes.func.isRequired,
};

const MemberRow = ({
	member,
	isCurrentUser,
	isOwner,
	isAdminOrOwner,
	isEditingBudget,
	isConfirmingRemove,
	transferDataOnRemove,
	onToggleBudget,
	onToggleRemove,
	onRemove,
	onRoleChange,
	onTransfer,
	onTransferDataToggle,
	changeRolePending,
	removeMutation,
}) => {
	const displayName = member.fullName || member.email;
	const avatarInitial = (member.email || '?')[0].toUpperCase();

	return (
		<div className="px-4 py-3">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500">
						<span className="text-xs font-semibold text-white">{avatarInitial}</span>
					</div>
					<div className="min-w-0">
						<p className="truncate text-sm font-medium text-text">
							{displayName}
							{isCurrentUser && <span className="ml-1 text-bgBase-800">(Du)</span>}
						</p>
						{member.fullName && <p className="truncate text-xs text-bgBase-800">{member.email}</p>}
					</div>
				</div>

				<div className="flex flex-shrink-0 items-center gap-3">
					<div className="hidden gap-3 whitespace-nowrap text-xs text-bgBase-800 sm:flex">
						<span>
							{formatCount(member.companyCredits)} / {formatCount(member.companyMonthlyBudget)} Firmen
						</span>
						<span>
							{formatCount(member.contactCredits)} / {formatCount(member.contactMonthlyBudget)} Kontakte
						</span>
					</div>

					<RoleDisplay
						member={member}
						isOwner={isOwner}
						isCurrentUser={isCurrentUser}
						onRoleChange={onRoleChange}
						changeRolePending={changeRolePending}
					/>

					{isAdminOrOwner && (
						<MemberActions
							member={member}
							isCurrentUser={isCurrentUser}
							onTransfer={onTransfer}
							onToggleBudget={onToggleBudget}
							onToggleRemove={onToggleRemove}
						/>
					)}
				</div>
			</div>

			{isEditingBudget && <MemberBudgetEditor member={member} onClose={() => onToggleBudget(null)} />}

			{isConfirmingRemove && (
				<RemoveConfirmation
					member={member}
					transferDataOnRemove={transferDataOnRemove}
					onTransferDataToggle={onTransferDataToggle}
					onToggleRemove={onToggleRemove}
					onRemove={onRemove}
					removeMutation={removeMutation}
				/>
			)}
		</div>
	);
};

MemberRow.propTypes = {
	member: PropTypes.object.isRequired,
	isCurrentUser: PropTypes.bool.isRequired,
	isOwner: PropTypes.bool.isRequired,
	isAdminOrOwner: PropTypes.bool.isRequired,
	isEditingBudget: PropTypes.bool.isRequired,
	isConfirmingRemove: PropTypes.bool.isRequired,
	transferDataOnRemove: PropTypes.bool.isRequired,
	onToggleBudget: PropTypes.func.isRequired,
	onToggleRemove: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	onRoleChange: PropTypes.func.isRequired,
	onTransfer: PropTypes.func.isRequired,
	onTransferDataToggle: PropTypes.func.isRequired,
	changeRolePending: PropTypes.bool.isRequired,
	removeMutation: PropTypes.object.isRequired,
};

export default MemberRow;
